import { useNavigate } from 'react-router-dom';
import { Patient } from '../lib/db';

interface PatientCardProps {
  patient: Patient;
}

export default function PatientCard({ patient }: PatientCardProps) {
  const navigate = useNavigate();
  const priorityConfig =
    patient.priority === 'High' ? {
      bg: 'bg-red-50',
      border: 'border-red-200',
      badgeClass: 'badge-high'
    } :
    patient.priority === 'Medium' ? {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      badgeClass: 'badge-medium'
    } : {
      bg: 'bg-peacockTeal-50',
      border: 'border-peacockTeal-200',
      badgeClass: 'badge-low'
    };

  return (
    <article
      className={`patient-card ${priorityConfig.bg} border-2 ${priorityConfig.border}`}
      aria-label={`Patient ${patient.name}, ${patient.priority} priority, ${patient.points || 0} points`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          {/* Patient Avatar & Name */}
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md"
              style={{
                background: 'linear-gradient(135deg, var(--color-primary-500) 0%, var(--color-primary-600) 100%)'
              }}
              aria-hidden="true"
            >
              {patient.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                {patient.name}
              </h3>
              {patient.lastUpdated && (
                <p className="text-xs text-gray-500">
                  <span className="sr-only">Last updated: </span>
                  Updated <time dateTime={new Date(patient.lastUpdated).toISOString()}>{new Date(patient.lastUpdated).toLocaleDateString()}</time>
                </p>
              )}
            </div>
          </div>

          {/* Priority & Points */}
          <div className="flex items-center gap-4">
            <span className={priorityConfig.badgeClass} aria-label={`Priority: ${patient.priority}`}>
              {patient.priority}
            </span>
            <div className="flex items-center gap-1.5" aria-label={`Patient has ${patient.points?.toLocaleString() || 0} reward points`}>
              <svg className="w-5 h-5 text-peacockTeal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              <span className="text-gradient font-semibold">{patient.points?.toLocaleString() || 0} Points</span>
            </div>
          </div>

          {/* Sync Status */}
          {patient.syncStatus === 'pending' && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-yellow-600" role="status" aria-live="polite">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Syncing changes...
            </div>
          )}
        </div>

        <div className="ml-4">
          <button
            onClick={() => navigate(`/patient/${patient.id}`)}
            className="btn btn-primary"
            aria-label={`View details for patient ${patient.name}`}
          >
            View Details
            <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </article>
  );
}
